import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RichTextEditor } from "./RichTextEditor";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { format, isBefore, startOfDay } from "date-fns";
import {
  Send, Paperclip, Clock, Loader2, Building2, User,
  X, MessageSquare, Folder, Upload, File, Eye, Undo2, CalendarIcon,
  FileText, Plane, Shield, Check, CheckCheck, RefreshCw, Plus
} from "lucide-react";
import { logActivity } from "@/lib/activityLogger";

interface SentEmailRecord {
  id: string;
  subject: string;
  body_html: string;
  body_text: string | null;
  sent_at: string;
  status: string;
  attachments: any;
  opened_at: string | null;
  recipient_email: string;
  type: 'sent';
}

interface InboundEmailRecord {
  id: string;
  from_email: string;
  from_name: string | null;
  subject: string;
  body_text: string | null;
  body_html: string | null;
  received_at: string;
  attachments: any;
  read_at: string | null;
  type: 'received';
}

interface ScheduledEmailRecord {
  id: string;
  subject: string;
  body_html: string;
  body_text: string | null;
  scheduled_for: string;
  created_at: string;
  status: string;
  attachments: any;
  error_message?: string | null;
  sent_at?: string | null;
  type: 'scheduled';
}

interface ActivityLogRecord {
  id: string;
  action_type: string;
  action_description: string;
  created_at: string;
  metadata: any;
  type: 'activity';
}

type ThreadItem = SentEmailRecord | InboundEmailRecord | ScheduledEmailRecord | ActivityLogRecord;

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body_html: string;
  category: string;
}

interface Attachment {
  filename: string;
  contentType: string;
  content?: string;
  storageBucket?: string;
  storagePath?: string;
  localObjectUrl?: string;
}

interface InquiryFile {
  id: string;
  name: string;
  storage_path: string;
  type: 'contract' | 'itinerary' | 'attachment' | 'admin';
  status: string;
  created_at: string;
}

interface AdminFile {
  id: string;
  name: string;
  storage_path: string;
  created_at: string;
}

interface InlineEmailPanelProps {
  inquiryId: string;
  inquiryName: string;
  inquiryEmail: string;
  occasion?: string | null;
  totalCost?: number | null;
  eventDatesJson?: any;
  onPreviewDocument?: (storagePath: string, name: string) => void;
}

const stripHtml = (html: string) => {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .trim();
};

export function InlineEmailPanel({
  inquiryId,
  inquiryName,
  inquiryEmail,
  occasion,
  totalCost,
  eventDatesJson,
  onPreviewDocument,
}: InlineEmailPanelProps) {
  const { toast } = useToast();
  const [threadItems, setThreadItems] = useState<ThreadItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [subject, setSubject] = useState("");
  const [recipients, setRecipients] = useState<{ email: string; name?: string }[]>([{ email: inquiryEmail, name: inquiryName }]);
  const [newRecipient, setNewRecipient] = useState("");
  const [activeTab, setActiveTab] = useState(inquiryEmail);
  const [messageBody, setMessageBody] = useState("");
  const [messageHtml, setMessageHtml] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [inquiryFiles, setInquiryFiles] = useState<InquiryFile[]>([]);
  const [adminFiles, setAdminFiles] = useState<AdminFile[]>([]);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [attachingFiles, setAttachingFiles] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>();
  const [scheduleTime, setScheduleTime] = useState("09:00");
  const [lastSentEmailId, setLastSentEmailId] = useState<string | null>(null);
  const [undoCountdown, setUndoCountdown] = useState(0);
  const [isUndoing, setIsUndoing] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [conversationGroups, setConversationGroups] = useState<{ name: string; emails: string[] }[]>([]);
  const [activeGroup, setActiveGroup] = useState<string>("all");
  const [newGroupName, setNewGroupName] = useState("");
  const [showGroupManager, setShowGroupManager] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const composeAreaRef = useRef<HTMLDivElement>(null);
  const initialLoadDone = useRef(false);

  const safeTime = (value?: string | null) => {
    const t = value ? new Date(value).getTime() : NaN;
    return Number.isFinite(t) ? t : 0;
  };

  const getThreadItemSortTime = (item: ThreadItem): number => {
    if (item.type === 'sent') return safeTime(item.sent_at);
    if (item.type === 'received') return safeTime(item.received_at);
    if (item.type === 'activity') return safeTime(item.created_at);
    if (item.status === 'sent') return safeTime(item.sent_at) || safeTime(item.created_at) || safeTime(item.scheduled_for);
    return safeTime(item.created_at) || safeTime(item.scheduled_for);
  };

  const fetchData = useCallback(async () => {
    const [sentRes, inboundRes, scheduledRes, activityRes, templatesRes] = await Promise.all([
      supabase.from("inquiry_emails").select("*").eq("inquiry_id", inquiryId),
      supabase.from("inquiry_inbound_emails").select("*").eq("inquiry_id", inquiryId),
      supabase.from("scheduled_emails").select("*").eq("inquiry_id", inquiryId),
      supabase.from("inquiry_activity_logs").select("*").eq("inquiry_id", inquiryId),
      supabase.from("email_templates").select("*").eq("is_active", true).order("category"),
    ]);

    if (templatesRes.data) setTemplates(templatesRes.data);

    // Filter out cancelled emails from thread view
    const sentEmails: ThreadItem[] = (sentRes.data || [])
      .filter(e => e.status !== 'cancelled')
      .map(e => ({ ...e, type: 'sent' as const }));
    const inboundEmailsRaw = inboundRes.data || [];
    const inboundEmails: ThreadItem[] = inboundEmailsRaw.map(e => ({ ...e, type: 'received' as const }));
    const scheduledEmails: ThreadItem[] = (scheduledRes.data || [])
      .filter(e => e.status !== 'cancelled')
      .map(e => ({ ...e, type: 'scheduled' as const }));
    
    // Calculate unread count (inbound emails without read_at)
    const unreadMessages = inboundEmailsRaw.filter(e => !e.read_at);
    setUnreadCount(unreadMessages.length);
    
    // Filter out email-related and minor document-related activity logs from thread view
    // KEEP contract lifecycle milestones: contract_email_opened, contract_viewed, document_signed, document_sent, payment_method_selected
    const hiddenActivityTypes = [
      'email_sent', 'email_cancelled', 'email_scheduled', 'scheduled_email_sent',
      'document_previewed', 'document_created', 'document_deleted', 'document_edited'
    ];
    const activityLogs: ThreadItem[] = (activityRes.data || [])
      .filter(e => !hiddenActivityTypes.includes(e.action_type))
      .map(e => ({ ...e, type: 'activity' as const }));

    const allItems = [...sentEmails, ...inboundEmails, ...scheduledEmails, ...activityLogs].sort((a, b) => {
      const ta = getThreadItemSortTime(a);
      const tb = getThreadItemSortTime(b);
      if (ta !== tb) return ta - tb;
      return String(a.id).localeCompare(String(b.id));
    });

    setThreadItems(allItems);
    setIsLoading(false);
    initialLoadDone.current = true;
    
    // Mark unread emails as read after fetching
    if (unreadMessages.length > 0) {
      const unreadIds = unreadMessages.map(e => e.id);
      supabase
        .from("inquiry_inbound_emails")
        .update({ read_at: new Date().toISOString() })
        .in("id", unreadIds)
        .then(() => {
          // Reset unread count after marking as read
          setUnreadCount(0);
        });
    }
  }, [inquiryId]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData();
    setIsRefreshing(false);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (scrollRef.current && threadItems.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [threadItems]);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel(`email-thread-inline-${inquiryId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "inquiry_emails", filter: `inquiry_id=eq.${inquiryId}` }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "inquiry_inbound_emails", filter: `inquiry_id=eq.${inquiryId}` }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "scheduled_emails", filter: `inquiry_id=eq.${inquiryId}` }, () => fetchData())
      .on("postgres_changes", { event: "*", schema: "public", table: "inquiry_activity_logs", filter: `inquiry_id=eq.${inquiryId}` }, () => fetchData())
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [inquiryId, fetchData]);

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const replaceTemplatePlaceholders = (text: string): string => {
    const eventDate = eventDatesJson?.[0]?.date || "";
    const cost = totalCost ? `$${totalCost.toLocaleString()}` : "";
    return text
      .replace(/\{\{name\}\}/g, inquiryName || "")
      .replace(/\{\{occasion\}\}/g, occasion || "your event")
      .replace(/\{\{event_date\}\}/g, eventDate)
      .replace(/\{\{total_cost\}\}/g, cost)
      .replace(/\{\{email\}\}/g, inquiryEmail || "");
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSubject(replaceTemplatePlaceholders(template.subject));
      const processedHtml = replaceTemplatePlaceholders(template.body_html);
      setMessageHtml(processedHtml);
      const strippedText = processedHtml
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n\n")
        .replace(/<[^>]*>/g, "")
        .trim();
      setMessageBody(strippedText);
    }
  };

  const handleSend = async (scheduled?: Date) => {
    if (!subject.trim() || !messageBody.trim()) {
      toast({ title: "Missing fields", description: "Subject and message are required", variant: "destructive" });
      return;
    }

    setIsSending(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const emailPayload: any = {
        inquiryId,
        recipientEmails: recipients.map(r => r.email),
        recipientEmail: recipients[0].email, // Fallback/primary
        recipientName: recipients[0].name || inquiryName,
        subject: subject.trim(),
        bodyHtml: messageHtml || `<p>${messageBody.replace(/\n/g, '<br>')}</p>`,
        bodyText: messageBody.trim(),
        templateUsed: selectedTemplate || null,
        attachments: attachments.map(att => ({
          filename: att.filename,
          contentType: att.contentType,
          content: att.content,
          storageBucket: att.storageBucket,
          storagePath: att.storagePath,
        })),
      };

      if (scheduled) {
        emailPayload.scheduledFor = scheduled.toISOString();
      }

      const res = await supabase.functions.invoke("send-crm-email", {
        body: emailPayload,
      });

      if (res.error) throw res.error;

      if (scheduled) {
        toast({ title: "Email scheduled! 📅", description: `Will send ${format(scheduled, "M/d 'at' h:mm a")}` });
        setShowSchedule(false);
      } else {
        toast({ title: "Email sent! ✉️", description: `Sent to ${inquiryEmail}` });
        
        // Start undo countdown for immediate sends
        if (res.data?.emailId) {
          setLastSentEmailId(res.data.emailId);
          setUndoCountdown(10);
        }
      }

      // No activity log for email send - emails appear in thread directly

      setSubject("");
      setMessageBody("");
      setMessageHtml("");
      setSelectedTemplate("");
      setAttachments([]);
      setScheduleDate(undefined);
      fetchData();
    } catch (err: any) {
      console.error("Send error:", err);
      toast({ title: "Failed to send", description: err.message, variant: "destructive" });
    } finally {
      setIsSending(false);
    }
  };

  // Undo countdown effect
  useEffect(() => {
    if (undoCountdown > 0) {
      const timer = setTimeout(() => setUndoCountdown(undoCountdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (undoCountdown === 0) {
      setLastSentEmailId(null);
    }
  }, [undoCountdown]);

  const handleUndo = async () => {
    if (!lastSentEmailId) return;
    setIsUndoing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const res = await supabase.functions.invoke("cancel-email", {
        body: { emailId: lastSentEmailId },
      });

      if (res.error) throw res.error;

      toast({ title: "Email recalled", description: "The email has been cancelled" });
      setUndoCountdown(0);
      setLastSentEmailId(null);
      fetchData();
    } catch (err: any) {
      toast({ title: "Recall failed", description: err.message, variant: "destructive" });
    } finally {
      setIsUndoing(false);
    }
  };

  const handleCancelScheduled = async (scheduledId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const res = await supabase.functions.invoke("cancel-email", {
        body: { scheduledEmailId: scheduledId },
      });

      if (res.error) throw res.error;
      toast({ title: "Scheduled email cancelled" });
      fetchData();
    } catch (err: any) {
      toast({ title: "Cancel failed", description: err.message, variant: "destructive" });
    }
  };

  const handleScheduleSend = () => {
    if (!scheduleDate) return;
    const [hours, minutes] = scheduleTime.split(':').map(Number);
    const scheduledDateTime = new Date(scheduleDate);
    scheduledDateTime.setHours(hours, minutes, 0, 0);
    
    if (scheduledDateTime <= new Date()) {
      toast({ title: "Invalid time", description: "Scheduled time must be in the future", variant: "destructive" });
      return;
    }
    
    handleSend(scheduledDateTime);
  };

  const fetchInquiryFiles = async () => {
    const { data } = await supabase
      .from("inquiry_contracts")
      .select("id, contract_number, storage_path, status, created_at")
      .eq("inquiry_id", inquiryId)
      .not("storage_path", "is", null);
    
    if (data) {
      setInquiryFiles(data.map(c => ({
        id: c.id,
        name: c.contract_number,
        storage_path: c.storage_path!,
        type: 'contract' as const,
        status: c.status,
        created_at: c.created_at,
      })));
    }
  };

  const fetchAdminFiles = async () => {
    const { data } = await supabase.storage.from("contracts").list("admin", { limit: 50 });
    if (data) {
      setAdminFiles(data.filter(f => !f.name.startsWith('.')).map(f => ({
        id: `admin-${f.name}`,
        name: f.name,
        storage_path: `admin/${f.name}`,
        created_at: f.created_at || '',
      })));
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    try {
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const storagePath = `${inquiryId}/attachments/${Date.now()}-${file.name}`;
          const { error } = await supabase.storage.from("contracts").upload(storagePath, file, { contentType: file.type || "application/octet-stream" });
          if (error) throw error;
          return { filename: file.name, contentType: file.type || "application/octet-stream", storageBucket: "contracts", storagePath, localObjectUrl: URL.createObjectURL(file) };
        })
      );
      setAttachments(prev => [...prev, ...uploaded]);
    } catch (err: any) {
      toast({ title: "Upload failed", description: err?.message, variant: "destructive" });
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const attachSelectedFiles = async () => {
    if (selectedFileIds.size === 0) return;
    setAttachingFiles(true);
    const newAttachments: Attachment[] = [];
    for (const fileId of selectedFileIds) {
      const isAdmin = fileId.startsWith('admin-');
      const adminFile = isAdmin ? adminFiles.find(f => f.id === fileId) : null;
      const inquiryFile = !isAdmin ? inquiryFiles.find(f => f.id === fileId) : null;
      const storagePath = adminFile?.storage_path || inquiryFile?.storage_path;
      if (!storagePath) {
        console.warn('File missing storage path:', fileId);
        continue;
      }
      
      // Detect content type from storage path extension
      const extension = storagePath.toLowerCase().split('.').pop() || '';
      const mimeTypes: Record<string, string> = {
        // Documents
        'pdf': 'application/pdf', 'html': 'text/html', 'htm': 'text/html',
        'doc': 'application/msword', 'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel', 'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint', 'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        // Text
        'txt': 'text/plain', 'md': 'text/markdown', 'json': 'application/json', 'csv': 'text/csv', 'xml': 'application/xml',
        // Images
        'png': 'image/png', 'jpg': 'image/jpeg', 'jpeg': 'image/jpeg', 'gif': 'image/gif', 'webp': 'image/webp', 'svg': 'image/svg+xml',
        // Audio
        'mp3': 'audio/mpeg', 'wav': 'audio/wav', 'm4a': 'audio/mp4', 'ogg': 'audio/ogg',
        // Video
        'mp4': 'video/mp4', 'webm': 'video/webm', 'mov': 'video/quicktime',
      };
      const contentType = mimeTypes[extension] || 'application/octet-stream';
      
      // Determine proper filename with extension based on storage path
      let filename: string;
      if (adminFile) {
        filename = adminFile.name;
      } else if (inquiryFile) {
        filename = `${inquiryFile.name}.${extension || 'html'}`;
      } else {
        filename = storagePath.split('/').pop() || 'document';
      }
      
      console.log('Attaching file:', { filename, storagePath, contentType });
      newAttachments.push({ filename, contentType, storageBucket: "contracts", storagePath });
    }
    setAttachments(prev => [...prev, ...newAttachments]);
    setSelectedFileIds(new Set());
    setShowFilePicker(false);
    setAttachingFiles(false);
    toast({ title: "Files attached", description: `${newAttachments.length} file(s) added` });
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'itinerary': return <Plane className="h-4 w-4 text-primary" />;
      case 'contract': return <FileText className="h-4 w-4 text-amber-600" />;
      default: return <File className="h-4 w-4 text-muted-foreground" />;
    }
  };

  // Drag and drop handlers for documents from DocumentsPanel
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('application/json') || e.dataTransfer.types.includes('vendor/contact')) {
      setIsDragOver(true);
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the compose area entirely
    const rect = composeAreaRef.current?.getBoundingClientRect();
    if (rect) {
      const { clientX, clientY } = e;
      if (
        clientX < rect.left ||
        clientX > rect.right ||
        clientY < rect.top ||
        clientY > rect.bottom
      ) {
        setIsDragOver(false);
      }
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    try {
      // Check for vendor contact drop first
      const vendorData = e.dataTransfer.getData('vendor/contact');
      if (vendorData) {
        handleVendorDrop(e);
        return;
      }

      const jsonData = e.dataTransfer.getData('application/json');
      if (!jsonData) return;

      const draggedDoc = JSON.parse(jsonData);
      console.log('📥 Document dropped:', draggedDoc);
      
      // Check if this document is already attached
      const alreadyAttached = attachments.some(
        att => att.storagePath === draggedDoc.storagePath
      );
      
      if (alreadyAttached) {
        toast({ title: "Already attached", description: "This document is already in attachments" });
        return;
      }

      // Add the document to attachments
      const newAttachment: Attachment = {
        filename: draggedDoc.filename,
        contentType: draggedDoc.contentType,
        storageBucket: draggedDoc.storageBucket,
        storagePath: draggedDoc.storagePath,
      };
      console.log('✅ Created attachment:', newAttachment);

      setAttachments(prev => [...prev, newAttachment]);
      toast({ title: "Document attached", description: draggedDoc.filename });
    } catch (err) {
      console.error('Error handling drop:', err);
    }
  };

  const addRecipient = (email?: string, name?: string) => {
    const emailToAdd = (email || newRecipient).trim();
    if (emailToAdd && !recipients.some(r => r.email === emailToAdd)) {
      const updated = [...recipients, { email: emailToAdd, name }];
      setRecipients(updated);
      if (!email) setNewRecipient("");
      
      // Auto-detect non-client recipients and create/update groups
      if (emailToAdd !== inquiryEmail) {
        const nonClientEmails = updated.filter(r => r.email !== inquiryEmail).map(r => r.email);
        const existingVendorGroup = conversationGroups.find(g => g.name === 'Vendors');
        if (existingVendorGroup) {
          // Update existing vendor group
          setConversationGroups(prev => prev.map(g => 
            g.name === 'Vendors' ? { ...g, emails: [...new Set([...g.emails, emailToAdd])] } : g
          ));
        } else if (nonClientEmails.length >= 1) {
          // Auto-create a Vendors group when first non-client email is added
          setConversationGroups(prev => [...prev, { name: 'Vendors', emails: nonClientEmails }]);
          toast({ title: "Group auto-created", description: `"Vendors" group with ${nonClientEmails.length} recipient(s)` });
        }
      }
    }
  };

  const removeRecipient = (email: string) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter(r => r.email !== email));
      if (activeTab === email) setActiveTab(recipients[0].email);
    }
  };

  // Drag and drop for vendor cards to add recipient
  const handleVendorDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const vendorData = e.dataTransfer.getData("vendor/contact");
    if (vendorData) {
      try {
        const vendor = JSON.parse(vendorData);
        if (vendor.email) {
          addRecipient(vendor.email, vendor.name);
          toast({ title: "Recipient added", description: `${vendor.name} (${vendor.email})` });
        }
      } catch (err) {
        console.error("Failed to parse vendor data", err);
      }
    }
  };

  // Filter threads by active tab (recipient)
  const filteredThreadItems = threadItems.filter(item => {
    if (item.type === 'activity') return true; // Show activity logs always or filter? Let's keep them global for context
    if (item.type === 'sent') return true; // Sent items usually go to main, but maybe filter by recipient if we tracked it?
    // Ideally we'd filter by to/from, but for now let's just show all for simplicity until we update the DB schema to track array of recipients
    return true; 
  });

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Thread Header with Refresh + Group Tabs */}
      <div className="border-b">
        <div className="flex items-center justify-between px-2 py-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground">Conversation</span>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center h-[18px] min-w-[18px] px-1 text-[11px] font-bold bg-red-500 text-white rounded-full leading-none">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowGroupManager(!showGroupManager)}
              className="h-5 px-1.5 text-[9px]"
            >
              Groups
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="h-5 w-5 p-0"
            >
              <RefreshCw className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        {/* Group Tabs */}
        {conversationGroups.length > 0 && (
          <div className="flex items-center gap-1 px-2 pb-1 overflow-x-auto">
            <button
              className={`text-[9px] px-2 py-0.5 rounded-full border transition-colors ${activeGroup === 'all' ? 'bg-primary/15 border-primary/30 text-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
              onClick={() => { setActiveGroup('all'); setRecipients([{ email: inquiryEmail, name: inquiryName }]); }}
            >
              All
            </button>
            {conversationGroups.map((g, idx) => (
              <button
                key={idx}
                className={`text-[9px] px-2 py-0.5 rounded-full border transition-colors ${activeGroup === g.name ? 'bg-primary/15 border-primary/30 text-primary' : 'border-border text-muted-foreground hover:bg-muted'}`}
                onClick={() => {
                  setActiveGroup(g.name);
                  setRecipients(g.emails.map(e => ({ email: e })));
                }}
              >
                {g.name} ({g.emails.length})
              </button>
            ))}
          </div>
        )}
        {/* Group Manager */}
        {showGroupManager && (
          <div className="px-2 pb-2 space-y-2 border-t pt-2">
            <div className="flex items-center gap-1">
              <Input
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                placeholder="Group name (e.g. Vendor Team)"
                className="h-6 text-[10px] flex-1"
                onKeyDown={e => {
                  if (e.key === 'Enter' && newGroupName.trim()) {
                    setConversationGroups([...conversationGroups, { name: newGroupName.trim(), emails: recipients.map(r => r.email) }]);
                    setNewGroupName("");
                    toast({ title: "Group created", description: `"${newGroupName.trim()}" with ${recipients.length} recipient(s)` });
                  }
                }}
              />
              <Button
                size="sm"
                variant="outline"
                className="h-6 text-[9px] px-2"
                disabled={!newGroupName.trim()}
                onClick={() => {
                  setConversationGroups([...conversationGroups, { name: newGroupName.trim(), emails: recipients.map(r => r.email) }]);
                  setNewGroupName("");
                  toast({ title: "Group created", description: `"${newGroupName.trim()}" with ${recipients.length} recipient(s)` });
                }}
              >
                Save Current TO as Group
              </Button>
            </div>
            {conversationGroups.length > 0 && (
              <div className="space-y-1">
                {conversationGroups.map((g, idx) => (
                  <div key={idx} className="flex items-center justify-between text-[10px] bg-muted/30 rounded px-2 py-1">
                    <span className="font-medium">{g.name}: {g.emails.join(', ')}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-4 w-4 p-0 text-destructive"
                      onClick={() => {
                        setConversationGroups(conversationGroups.filter((_, i) => i !== idx));
                        if (activeGroup === g.name) setActiveGroup('all');
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Thread - Flex grow to fill available space */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-2 py-2 space-y-1 min-h-0"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : (() => {
          const activeGroupEmails = activeGroup !== 'all' 
            ? conversationGroups.find(g => g.name === activeGroup)?.emails || []
            : [];
          const filteredItems = activeGroup === 'all' 
            ? threadItems 
            : threadItems.filter(item => {
                if (item.type === 'activity') return true;
                if (item.type === 'sent') return activeGroupEmails.includes(item.recipient_email);
                if (item.type === 'received') return activeGroupEmails.includes(item.from_email);
                return true;
              });
          return filteredItems.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground text-xs">
              <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-40" />
              No messages {activeGroup !== 'all' ? `in "${activeGroup}"` : 'yet'}
            </div>
          ) : (
          filteredItems.map((item) => {
            // Activity Log - Compact inline divider
            if (item.type === 'activity') {
              return (
              <div key={item.id} className="flex items-center gap-1.5 py-0.5 my-0.5">
                  <div className="shrink-0 w-4 border-t border-dashed border-muted-foreground/20" />
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-muted/40 text-[9px] text-muted-foreground">
                    <span className="break-words whitespace-normal">{item.action_description}</span>
                    <span className="text-muted-foreground/60 shrink-0">· {format(new Date(item.created_at), "M/d h:mm a")}</span>
                  </div>
                  <div className="shrink-0 w-4 border-t border-dashed border-muted-foreground/20" />
                </div>
              );
            }

            // Scheduled Email - Compact inline style
            if (item.type === 'scheduled') {
              const isPending = item.status === 'pending';
              const isSent = item.status === 'sent';
              const isCancelled = item.status === 'cancelled';
              const isFailed = item.status === 'failed';
              
              return (
                <div key={item.id} className={`flex items-center gap-1.5 py-0.5 my-0.5 text-[9px] ${isCancelled ? 'opacity-50' : ''}`}>
                  <div className="flex-1 border-t border-dashed border-muted-foreground/20" />
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${
                    isSent ? 'bg-primary/10 text-primary' :
                    isCancelled ? 'bg-muted/40 text-muted-foreground' :
                    isFailed ? 'bg-destructive/10 text-destructive' :
                    'bg-muted/50 text-muted-foreground'
                  }`}>
                    <Clock className="h-2.5 w-2.5" />
                    <span className={`truncate max-w-[140px] ${isCancelled ? 'line-through' : ''}`}>{item.subject}</span>
                    <span className="text-[8px] opacity-70">
                      {isPending && `→ ${format(new Date(item.scheduled_for), "M/d h:mm a")}`}
                      {isSent && '✓ sent'}
                      {isCancelled && 'cancelled'}
                      {isFailed && 'failed'}
                    </span>
                    {isPending && (
                      <button onClick={() => handleCancelScheduled(item.id)} className="hover:text-destructive">
                        <X className="h-2.5 w-2.5" />
                      </button>
                    )}
                  </div>
                  <div className="flex-1 border-t border-dashed border-muted-foreground/20" />
                </div>
              );
            }

            // Sent / Received Email - Clean message bubbles
            // Convention: Admin-sent messages on RIGHT, client replies on LEFT
            const isSent = item.type === 'sent';
            const isCancelledEmail = isSent && item.status === 'cancelled';
            const timestamp = isSent ? item.sent_at : item.received_at;
            const bodyText = item.body_text || (item.body_html ? stripHtml(item.body_html) : '');
            const isLong = bodyText.length > 100;
            const isExpanded = expandedItems.has(item.id);
            const displayText = isLong && !isExpanded ? bodyText.slice(0, 100) + '…' : bodyText;
            const hasAttachments = item.attachments && Array.isArray(item.attachments) && item.attachments.length > 0;
            const isOpened = isSent && item.opened_at;
            const senderLabel = isSent ? 'You' : (item.from_name || item.from_email || inquiryName);

            return (
              <div key={item.id} className={`flex items-start gap-1.5 ${isSent ? 'flex-row-reverse' : ''} ${isCancelledEmail ? 'opacity-50' : ''}`}>
                <div className={`shrink-0 h-5 w-5 rounded-full flex items-center justify-center ${isSent ? 'bg-primary/10' : 'bg-accent'}`}>
                  {isSent ? <Building2 className="h-2.5 w-2.5 text-primary" /> : <User className="h-2.5 w-2.5 text-accent-foreground" />}
                </div>
                <div className={`flex-1 min-w-0 ${isSent ? 'text-right' : ''}`}>
                  <div 
                    className={`inline-block max-w-full text-left rounded-lg px-2.5 py-1.5 text-[10px] cursor-pointer ${isSent ? 'bg-primary/10 border border-primary/20' : 'bg-accent/60 border border-accent/30'} ${isCancelledEmail ? 'opacity-60' : ''}`}
                    onClick={() => isLong && toggleExpand(item.id)}
                  >
                    <div className="flex items-center gap-1 mb-0.5 text-[8px] text-muted-foreground">
                      <span className={`font-semibold ${isSent ? 'text-primary' : 'text-accent-foreground'}`}>{senderLabel}</span>
                      <span className="mx-0.5">·</span>
                      <span className={`font-medium text-foreground truncate ${isCancelledEmail ? 'line-through' : ''}`}>{item.subject}</span>
                      <span className="ml-auto shrink-0 flex items-center gap-1">
                        {format(new Date(timestamp), "M/d h:mm a")}
                        {/* Read receipt indicator */}
                        {isSent && (
                          <span className={`flex items-center ${isOpened ? 'text-primary' : 'text-muted-foreground/50'}`} title={isOpened ? `Opened ${format(new Date(item.opened_at!), "M/d h:mm a")}` : 'Not opened yet'}>
                            {isOpened ? <CheckCheck className="h-2.5 w-2.5" /> : <Check className="h-2.5 w-2.5" />}
                          </span>
                        )}
                      </span>
                    </div>
                    <p className={`whitespace-pre-wrap break-words leading-tight ${isCancelledEmail ? 'line-through' : ''}`}>{displayText}</p>
                    {isLong && <button className="text-[8px] text-primary hover:underline mt-0.5">{isExpanded ? 'Show less' : 'Show more'}</button>}
                    {hasAttachments && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {item.attachments.map((att: any, idx: number) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('Attachment clicked:', att);
                              if (att.storagePath && onPreviewDocument) {
                                onPreviewDocument(att.storagePath, att.filename);
                              } else if (!att.storagePath) {
                                toast({ 
                                  title: "Preview unavailable", 
                                  description: "This attachment was sent before preview support was added",
                                  variant: "destructive"
                                });
                              }
                            }}
                            className="inline-flex items-center gap-0.5 bg-background/80 hover:bg-background border rounded px-1.5 py-0.5 text-[8px] transition-colors cursor-pointer hover:border-primary"
                          >
                            <Eye className="h-2 w-2" />
                            <span className="max-w-[80px] truncate">{att.filename}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
          );
        })()}
      </div>

      {/* Undo Banner */}
      {lastSentEmailId && undoCountdown > 0 && (
        <div className="mx-2 my-1 p-1.5 bg-amber-500/10 border border-amber-500/30 rounded flex items-center justify-between text-[10px]">
          <span className="text-amber-600">Recall available ({undoCountdown}s)</span>
          <Button variant="ghost" size="sm" onClick={handleUndo} disabled={isUndoing} className="h-5 px-2 text-[10px] text-amber-600">
            {isUndoing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
            Undo
          </Button>
        </div>
      )}

      {/* Compose Area - Drop Zone */}
      <div 
        ref={composeAreaRef}
        className={`border-t pt-2 px-2 space-y-1.5 transition-colors ${isDragOver ? 'bg-primary/10 border-primary border-dashed rounded-lg' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <div className="flex items-center justify-center gap-2 py-2 text-xs text-primary font-medium">
            <Paperclip className="h-4 w-4" />
            Drop document to attach
          </div>
        )}
        <div className="flex flex-col gap-2 p-2 bg-muted/20 rounded-md" onDrop={handleVendorDrop} onDragOver={e => e.preventDefault()}>
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[10px] text-muted-foreground font-medium mr-1">To:</span>
            {recipients.map((r, idx) => (
              <Badge key={idx} variant="secondary" className="text-[10px] h-5 px-1.5 gap-1 font-normal bg-background border">
                {r.name ? `${r.name} <${r.email}>` : r.email}
                {recipients.length > 1 && (
                  <X 
                    className="h-2.5 w-2.5 cursor-pointer opacity-60 hover:opacity-100 hover:text-destructive" 
                    onClick={() => removeRecipient(r.email)}
                  />
                )}
              </Badge>
            ))}
            <div className="flex items-center gap-1 min-w-[120px]">
              <Input 
                value={newRecipient} 
                onChange={e => setNewRecipient(e.target.value)} 
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addRecipient();
                  }
                }}
                placeholder="Add email..." 
                className="h-5 text-[10px] bg-transparent border-none shadow-none focus-visible:ring-0 p-0 placeholder:text-muted-foreground/50" 
              />
              <Button size="icon" variant="ghost" className="h-4 w-4" onClick={() => addRecipient()} disabled={!newRecipient.trim()}>
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
          
          <div className="flex gap-1.5">
            <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
              <SelectTrigger className="h-6 text-[10px] flex-1">
                <SelectValue placeholder="Template..." />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.id} value={t.id} className="text-xs">
                    <Badge variant="outline" className="text-[8px] h-3 mr-1">{t.category}</Badge>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject..." className="h-7 text-xs" />
        
        {/* Rich Text Editor */}
        <RichTextEditor
          content={messageHtml}
          onChange={(html, text) => {
            setMessageHtml(html);
            setMessageBody(text);
          }}
          placeholder="Write your message..."
          minHeight="100px"
        />
        
        {/* Action Buttons */}
        <div className="flex items-center gap-1 pt-1">
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg" />
          <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => { setShowFilePicker(true); fetchInquiryFiles(); fetchAdminFiles(); }}>
            <Paperclip className="h-3 w-3 mr-1" />
            Attach
          </Button>
          {attachments.length > 0 && <Badge variant="secondary" className="text-[9px] h-5 px-1.5">{attachments.length} file(s)</Badge>}
          
          <div className="flex-1" />
          
          <Popover open={showSchedule} onOpenChange={setShowSchedule}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-6 px-2 text-[10px]" disabled={isSending || !subject.trim() || !messageBody.trim()}>
                <Clock className="h-3 w-3 mr-1" />
                Schedule
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3" align="end">
              <div className="space-y-2">
                <p className="text-xs font-medium">Schedule send</p>
                <Calendar mode="single" selected={scheduleDate} onSelect={setScheduleDate} disabled={(date) => isBefore(startOfDay(date), startOfDay(new Date()))} className="rounded-md border text-xs" />
                <div className="flex items-center gap-2">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <Input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="h-7 text-xs" />
                </div>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => setShowSchedule(false)} className="flex-1 h-6 text-xs">Cancel</Button>
                  <Button size="sm" onClick={handleScheduleSend} disabled={!scheduleDate} className="flex-1 h-6 text-xs">
                    <CalendarIcon className="h-3 w-3 mr-1" />Schedule
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
          
          <Button onClick={() => handleSend()} disabled={isSending || !subject.trim() || !messageBody.trim()} size="sm" className="h-6 px-3 text-[10px]">
            {isSending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
            Send
          </Button>
        </div>
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {attachments.map((att, idx) => (
              <span key={idx} className="inline-flex items-center gap-0.5 bg-muted border rounded px-1 py-0.5 text-[8px]">
                <Paperclip className="h-2 w-2" />
                <span className="truncate max-w-[100px]">{att.filename}</span>
                {att.storagePath && onPreviewDocument && (
                  <button 
                    onClick={() => onPreviewDocument(att.storagePath!, att.filename)} 
                    className="ml-0.5 hover:text-primary"
                    title="Preview"
                  >
                    <Eye className="h-2 w-2" />
                  </button>
                )}
                <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))} className="ml-0.5 hover:text-destructive">
                  <X className="h-2 w-2" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* File Picker Dialog */}
      <Dialog open={showFilePicker} onOpenChange={setShowFilePicker}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5" /> Attach Files
            </DialogTitle>
            <DialogDescription>Select files from this inquiry, admin files, or upload new.</DialogDescription>
          </DialogHeader>
          <Tabs defaultValue="inquiry">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="inquiry" className="text-xs"><Folder className="h-3 w-3 mr-1" />Inquiry</TabsTrigger>
              <TabsTrigger value="admin" className="text-xs"><Shield className="h-3 w-3 mr-1" />Admin</TabsTrigger>
              <TabsTrigger value="upload" className="text-xs"><Upload className="h-3 w-3 mr-1" />Upload</TabsTrigger>
            </TabsList>
            <TabsContent value="inquiry">
              <ScrollArea className="h-[200px]">
                {inquiryFiles.length === 0 ? (
                  <p className="text-center text-muted-foreground text-xs py-4">No documents found</p>
                ) : (
                  <div className="space-y-1">
                    {inquiryFiles.map(file => (
                      <Card 
                        key={file.id} 
                        className={`cursor-pointer transition-colors ${selectedFileIds.has(file.id) ? 'border-primary bg-primary/5' : ''}`}
                        onClick={() => {
                          setSelectedFileIds(prev => {
                            const next = new Set(prev);
                            if (next.has(file.id)) next.delete(file.id);
                            else next.add(file.id);
                            return next;
                          });
                        }}
                      >
                        <CardContent className="p-2 flex items-center gap-2">
                          {getFileIcon(file.type)}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{file.name}</p>
                            <p className="text-[10px] text-muted-foreground">{file.status}</p>
                          </div>
                          {selectedFileIds.has(file.id) && <Check className="h-4 w-4 text-primary" />}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
            <TabsContent value="admin">
              <ScrollArea className="h-[200px]">
                {adminFiles.length === 0 ? (
                  <p className="text-center text-muted-foreground text-xs py-4">No admin files</p>
                ) : (
                  <div className="space-y-1">
                    {adminFiles.map(file => (
                      <Card 
                        key={file.id} 
                        className={`cursor-pointer transition-colors ${selectedFileIds.has(file.id) ? 'border-primary bg-primary/5' : ''}`}
                        onClick={() => {
                          setSelectedFileIds(prev => {
                            const next = new Set(prev);
                            if (next.has(file.id)) next.delete(file.id);
                            else next.add(file.id);
                            return next;
                          });
                        }}
                      >
                        <CardContent className="p-2 flex items-center gap-2">
                          <File className="h-4 w-4 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{file.name}</p>
                          </div>
                          {selectedFileIds.has(file.id) && <Check className="h-4 w-4 text-primary" />}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
            <TabsContent value="upload">
              <div className="py-6 text-center">
                <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Select Files
                </Button>
                <p className="text-xs text-muted-foreground mt-2">PDF, DOC, images supported</p>
              </div>
            </TabsContent>
          </Tabs>
          {selectedFileIds.size > 0 && (
            <Button onClick={attachSelectedFiles} disabled={attachingFiles} className="w-full">
              {attachingFiles ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Paperclip className="h-4 w-4 mr-2" />}
              Attach {selectedFileIds.size} file(s)
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
